import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useToast } from '@kanda-libs/ks-design-library';
import {
  Credit,
  FinanceRate,
  Job,
  actions,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import useApiError from 'hooks/useApiError';
import {
  ALWAYS_SHOW_OMNI_ERROR_CODES,
  ERROR_1190,
  MAXIMUM_OMNI_ERROR_CODE_TO_DISPLAY,
} from 'pages/ApplyDeprecated/YourInfo/constants';
import { capitalise, extractErrorMessage, getErrorCode } from 'utils';

import { getLoanPayments } from './helpers';

export interface NewRateHook {
  submit: (
    id: string,
    credit: Credit,
    deposit: number,
    rate: FinanceRate,
    successCallback: () => void,
    errorCallback?: () => void,
  ) => void;
  isSubmitting: boolean;
}

function useNewRate(): NewRateHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { showError } = useToast();
  const dispatch = useAppDispatch();
  const handleApiError = useApiError();
  const { push } = useHistory();

  const submit = useCallback(
    (
      id: string,
      credit: Credit,
      deposit: number,
      rate: FinanceRate,
      successCallback?: () => void,
      errorCallback?: () => void,
    ) => {
      setIsSubmitting(true);
      dispatch(
        actions.applyJob({
          body: {
            checkout_option: 'apply_for_finance',
            finance_option: rate,
            deposit_value: { currency: 'GBP', amount: deposit },
          },
          params: { id },
          onError: (error) => {
            handleApiError(error || 'Error completing finance application');
            if (errorCallback) errorCallback();
            setIsSubmitting(false);
          },
          onSuccess: (job: Job) => {
            const loanPayments = getLoanPayments(job);
            const newCreditId = loanPayments?.[0]?.id;
            if (!newCreditId) {
              showError(
                'Issue submitting new rate - no cached credit data. Please reach out to Kanda at help@kanda.co.uk',
              );
              if (errorCallback) errorCallback();
              setIsSubmitting(false);
              return;
            }
            dispatch(
              actions.putCredit({
                body: {
                  ...credit,
                  finance_option: rate,
                },
                params: { id: newCreditId },
                forceReload: true,
                onError: (error) => {
                  handleApiError(
                    error || 'Error completing finance application',
                  );
                  if (errorCallback) errorCallback();
                  setIsSubmitting(false);
                },
                onSuccess: () => {
                  dispatch(
                    actions.applyCredit({
                      params: {
                        id: newCreditId,
                      },
                      forceReload: true,
                      onError: (error) => {
                        const code = getErrorCode(error.message);
                        const message = extractErrorMessage(error);

                        if (message.includes('1190')) {
                          showError(ERROR_1190);
                          return;
                        }

                        if (
                          code &&
                          (code < MAXIMUM_OMNI_ERROR_CODE_TO_DISPLAY ||
                            ALWAYS_SHOW_OMNI_ERROR_CODES.indexOf(code) !== -1)
                        ) {
                          if (message) {
                            showError(capitalise(message));
                            return;
                          }
                        }
                        showError('Error completing finance application');
                        if (errorCallback) errorCallback();
                        setIsSubmitting(false);
                      },
                      onSuccess: () => {
                        dispatch(
                          actions.getJob({
                            params: { id },
                            forceReload: true,
                            onSuccess: () => {
                              if (successCallback) successCallback();
                              setIsSubmitting(false);
                              push(URLS.status.replace(':id?', id));
                            },
                          }),
                        );
                      },
                    }),
                  );
                },
              }),
            );
          },
        }),
      );
    },
    [dispatch, handleApiError, showError, push],
  );

  return {
    submit,
    isSubmitting,
  };
}

export default useNewRate;
